import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import SafeIcon from '../common/SafeIcon';
import IframeViewer from './IframeViewer';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiImage, FiEye } = FiIcons;

const ProjectDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { project } = location.state || {};
  const [showIframe, setShowIframe] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (!project) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center px-4">
          <h2 className={`text-xl sm:text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Project not found
          </h2>
          <button
            onClick={() => navigate('/')}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const handlePreviewClick = () => {
    if (project.link) {
      setShowIframe(true);
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className={`sticky top-0 z-50 backdrop-blur-md border-b transition-colors duration-300 ${
          isDark 
            ? 'bg-gray-900/80 border-gray-800' 
            : 'bg-white/80 border-gray-200'
        }`}
      >
        <div className="px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/')}
              className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 rounded-lg transition-colors ${
                isDark 
                  ? 'bg-gray-800 hover:bg-gray-700 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
              }`}
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
              <span className="text-sm sm:text-base">Back</span>
            </motion.button>

            {project.link && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePreviewClick}
                className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                <SafeIcon icon={FiEye} className="w-4 h-4" />
                <span className="text-sm sm:text-base">Preview</span>
              </motion.button>
            )}
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="px-3 sm:px-4 py-4 sm:py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          {/* Project Image */}
          <div className="mb-6 sm:mb-8">
            <div className="aspect-video rounded-xl sm:rounded-2xl overflow-hidden">
              {project.pix ? (
                <img
                  src={project.pix}
                  alt={project.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`w-full h-full flex items-center justify-center ${
                project.pix ? 'hidden' : 'flex'
              } ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <SafeIcon icon={FiImage} className={`w-16 sm:w-24 h-16 sm:h-24 ${
                  isDark ? 'text-gray-600' : 'text-gray-400'
                }`} />
              </div>
            </div>
          </div>

          {/* Project Info */}
          <div className={`rounded-xl sm:rounded-2xl p-4 sm:p-8 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-start justify-between mb-4 sm:mb-6">
              <div>
                <h1 className={`text-2xl sm:text-4xl md:text-5xl font-black mb-3 sm:mb-4 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  {project.name || 'Untitled Project'}
                </h1>
                {project.cat && (
                  <span className={`px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-medium ${
                    isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {project.cat}
                  </span>
                )}
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className={`text-base sm:text-lg leading-relaxed ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {project.desc || 'No description available for this project.'}
              </p>
            </div>

            {project.link && (
              <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-gray-200 dark:border-gray-700">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handlePreviewClick}
                  className="w-full sm:w-auto inline-flex items-center justify-center space-x-2 sm:space-x-3 px-6 sm:px-8 py-3 sm:py-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors font-semibold text-base sm:text-lg"
                >
                  <SafeIcon icon={FiEye} className="w-5 h-5" />
                  <span>Live Preview</span>
                </motion.button>
              </div>
            )}
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className={`py-4 sm:py-8 border-t ${isDark ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'}`}>
        <div className="px-3 sm:px-4 text-center">
          <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            © 2025 Powered by <span className="text-orange-500 font-bold">Angry Branding</span>
          </p>
        </div>
      </footer>

      {/* Iframe Viewer */}
      <IframeViewer
        isOpen={showIframe}
        onClose={() => setShowIframe(false)}
        url={project.link}
        projectName={project.name}
      />
    </div>
  );
};

export default ProjectDetail;